import { Node, mergeAttributes } from "@tiptap/core";

/**
 * Custom TipTap node for polls in newsletters.
 *
 * In the editor: NodeView shows a styled poll with question + options + edit/delete.
 * In email HTML: renders as data attributes — newsletter-helpers converts to
 * clickable vote links with recipient email merge tags.
 *
 * Supports optional free-text response (renders as "Other" button that links
 * to a form page where the recipient can type their answer).
 */
export const PollNode = Node.create({
  name: "poll",
  group: "block",
  atom: true,
  draggable: true,

  addAttributes() {
    return {
      pollId: { default: null }, // auto-generated on insert
      question: { default: "What do you think?" },
      options: { default: ["Option 1", "Option 2"] }, // JSON array
      allowFreeText: { default: false },
      freeTextLabel: { default: "Other / Write your own" },
    };
  },

  parseHTML() {
    return [
      {
        tag: "div[data-poll]",
        getAttrs: (dom) => ({
          pollId: dom.getAttribute("data-poll"),
          question: dom.getAttribute("data-question") || "What do you think?",
          options: JSON.parse(dom.getAttribute("data-options") || '["Option 1","Option 2"]'),
          allowFreeText: dom.getAttribute("data-free-text") === "true",
          freeTextLabel: dom.getAttribute("data-free-text-label") || "Other / Write your own",
        }),
      },
    ];
  },

  // renderHTML is used for email output (editor.getHTML()) — stores data for post-processing
  renderHTML({ node }) {
    const { pollId, question, options, allowFreeText, freeTextLabel } = node.attrs;
    return [
      "div",
      {
        "data-poll": pollId,
        "data-question": question,
        "data-options": JSON.stringify(options),
        "data-free-text": allowFreeText ? "true" : "false",
        "data-free-text-label": freeTextLabel,
        style: "text-align:center;margin:16px 0;",
      },
      ["p", { style: "font-weight:600;font-size:14px;" }, question],
      ...options.map((opt, i) => [
        "a",
        {
          href: `{{poll_vote_url_${pollId}_${i}}}`,
          style:
            "display:block;margin:3px auto;padding:7px 24px;" +
            "background-color:#4E95D9;color:#ffffff;text-decoration:none;" +
            "font-family:Calibri,Arial,sans-serif;font-size:13px;font-weight:600;" +
            "border-radius:5px;width:fit-content;",
        },
        opt,
      ]),
    ];
  },

  // NodeView for the editor — shows styled poll preview
  addNodeView() {
    return ({ node, editor, getPos }) => {
      const { pollId, question, options, allowFreeText, freeTextLabel } = node.attrs;

      const dom = document.createElement("div");
      dom.setAttribute("data-poll", pollId);
      dom.style.cssText =
        "margin:16px 0;padding:14px;border:2px dashed rgba(78,149,217,0.4);" +
        "border-radius:8px;background:rgba(78,149,217,0.04);position:relative;";

      // Poll label
      const label = document.createElement("div");
      label.style.cssText =
        "font-size:9px;font-weight:700;text-transform:uppercase;letter-spacing:1px;" +
        "color:#4E95D9;margin-bottom:4px;";
      label.textContent = allowFreeText ? "POLL + FREE TEXT" : "POLL";
      dom.appendChild(label);

      // Question
      const q = document.createElement("div");
      q.style.cssText =
        "font-size:14px;font-weight:600;color:#213D61;margin-bottom:8px;" +
        "font-family:Georgia,serif;";
      q.textContent = question;
      dom.appendChild(q);

      // Options
      const optContainer = document.createElement("div");
      optContainer.style.cssText = "display:flex;flex-wrap:wrap;gap:6px;justify-content:center;";
      for (const opt of options) {
        const btn = document.createElement("div");
        btn.style.cssText =
          "display:inline-block;padding:7px 18px;background-color:#4E95D9;" +
          "color:#ffffff;font-family:Calibri,Arial,sans-serif;font-size:13px;" +
          "font-weight:600;border-radius:5px;cursor:default;";
        btn.textContent = opt;
        optContainer.appendChild(btn);
      }

      // Free-text button preview
      if (allowFreeText) {
        const ftBtn = document.createElement("div");
        ftBtn.style.cssText =
          "display:inline-block;padding:7px 18px;background-color:transparent;" +
          "color:#4E95D9;font-family:Calibri,Arial,sans-serif;font-size:13px;" +
          "font-weight:600;border-radius:5px;cursor:default;" +
          "border:2px solid #4E95D9;";
        ftBtn.textContent = freeTextLabel;
        optContainer.appendChild(ftBtn);
      }
      dom.appendChild(optContainer);

      // Edit button
      const editBtn = document.createElement("button");
      editBtn.innerHTML = "&#9998;";
      editBtn.title = "Edit poll";
      editBtn.style.cssText =
        "position:absolute;top:8px;right:36px;width:24px;height:24px;" +
        "border-radius:50%;border:none;background:rgba(78,149,217,0.9);" +
        "color:white;font-size:13px;cursor:pointer;" +
        "display:flex;align-items:center;justify-content:center;" +
        "opacity:0;transition:opacity 0.15s;z-index:2;";
      editBtn.addEventListener("click", (e) => {
        e.preventDefault();
        e.stopPropagation();
        const newQuestion = window.prompt("Poll question:", question);
        if (newQuestion === null) return;
        const newOptions = window.prompt(
          "Options (comma-separated):",
          options.join(", "),
        );
        if (newOptions === null) return;
        const parsed = newOptions
          .split(",")
          .map((s) => s.trim())
          .filter(Boolean);
        if (parsed.length < 2) {
          alert("Please provide at least 2 options.");
          return;
        }
        const newFreeText = window.confirm("Include a free-text response option?");
        const pos = getPos();
        editor
          .chain()
          .focus()
          .command(({ tr }) => {
            tr.setNodeMarkup(pos, undefined, {
              ...node.attrs,
              question: newQuestion || question,
              options: parsed,
              allowFreeText: newFreeText,
            });
            return true;
          })
          .run();
      });

      // Delete button
      const deleteBtn = document.createElement("button");
      deleteBtn.innerHTML = "&times;";
      deleteBtn.title = "Remove poll";
      deleteBtn.style.cssText =
        "position:absolute;top:8px;right:8px;width:24px;height:24px;" +
        "border-radius:50%;border:none;background:rgba(0,0,0,0.6);" +
        "color:white;font-size:16px;cursor:pointer;" +
        "display:flex;align-items:center;justify-content:center;" +
        "opacity:0;transition:opacity 0.15s;z-index:2;";
      deleteBtn.addEventListener("click", (e) => {
        e.preventDefault();
        e.stopPropagation();
        const pos = getPos();
        editor.chain().focus().deleteRange({ from: pos, to: pos + node.nodeSize }).run();
      });

      // Show controls on hover
      dom.addEventListener("mouseenter", () => {
        editBtn.style.opacity = "1";
        deleteBtn.style.opacity = "1";
      });
      dom.addEventListener("mouseleave", () => {
        editBtn.style.opacity = "0";
        deleteBtn.style.opacity = "0";
      });

      dom.appendChild(editBtn);
      dom.appendChild(deleteBtn);

      return { dom };
    };
  },

  addCommands() {
    return {
      setPoll:
        (attrs) =>
        ({ chain }) => {
          return chain()
            .insertContent({
              type: this.name,
              attrs: {
                pollId: attrs.pollId || generatePollId(),
                ...attrs,
              },
            })
            .run();
        },
    };
  },
});

function generatePollId() {
  return "poll-" + Date.now().toString(36) + Math.random().toString(36).slice(2, 6);
}
